import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { getEnterpriseStationIdFromLocalStorage } from '../enterprise-context';

@Injectable({
  providedIn: 'root'
})
export class StockService {
    private apiUrl = environment.stock_url;
    private stockBaseUrl = `${this.apiUrl.replace(/\/$/, '')}/api/stock`;
    private mouvementBaseUrl = `${this.apiUrl.replace(/\/$/, '')}/api/mouvements-stock`;
    private productBaseUrl = `${this.apiUrl.replace(/\/$/, '')}/api/stock/products`;
    private erpCatalogProductsUrl = `${String(environment.apiUrl ?? this.apiUrl).replace(/\/$/, '')}/api/products/`;
    private stockTransfertsUrl = `${String(environment.transfertsEntrepotsErpBaseUrl ?? '').replace(/\/$/, '')}/api/stock-transferts`;
    private readonly erpStockResolveCache = new Map<string, Observable<number | null>>();

    constructor(private http: HttpClient) { }

    private catchHttpNotFoundAsNull<T>(): (source: Observable<T>) => Observable<T | null> {
        return (source) =>
            source.pipe(
                catchError((error) => (error?.status === 404 ? of(null) : of(null)))
            );
    }

    private getStationUserIdsFromStorage(): { stationId: number; userId: number } {
        try {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const userId = Number(currentUser?.id) || 0;
            // Station connectée (isConnecte / station_id), pas stations[0]
            const stationId = getEnterpriseStationIdFromLocalStorage();
            return { userId, stationId };
        } catch {
            return { userId: 0, stationId: 0 };
        }
    }

    private applySessionToPayloadBody(payload: Record<string, unknown>): void {
        const { stationId, userId } = this.getStationUserIdsFromStorage();
        if (stationId > 0) {
            payload.station_id = stationId;
        }
        if (userId > 0) {
            payload.user_id = userId;
        }
    }

    private appendStationUserQueryParams(base?: HttpParams): HttpParams {
        const { stationId, userId } = this.getStationUserIdsFromStorage();
        let p = base ?? new HttpParams();
        if (stationId > 0) {
            p = p.set('station_id', String(stationId));
        }
        if (userId > 0) {
            p = p.set('user_id', String(userId));
        }
        return p;
    }

    private buildStationUserQueryParams(opts?: { stationId?: number; userId?: number }): HttpParams {
        const b = this.getStationUserIdsFromStorage();
        const stationId = opts?.stationId ?? b.stationId;
        const userId = opts?.userId ?? b.userId;
        let params = new HttpParams();
        if (stationId > 0) {
            params = params.set('station_id', String(stationId));
        }
        if (userId > 0) {
            params = params.set('user_id', String(userId));
        }
        return params;
    }

    buildStockProductTotalsApiUrlForLog(productPathId: number): string {
        const qs = this.appendStationUserQueryParams().toString();
        const path = `${this.stockBaseUrl}/product/${productPathId}`;
        return qs ? `${path}?${qs}` : path;
    }

    createOrUpdateStock(stockData: {
        entrepot_id: number;
        product_id: number;
        quantity: number;
    }): Observable<any>
    {
        // Convertir les valeurs en chaînes comme attendu par l'API
        const payload: Record<string, unknown> = {
            entrepot_id: stockData.entrepot_id,
            product_id: stockData.product_id,
            quantity: stockData.quantity.toString()
        };
        this.applySessionToPayloadBody(payload);
        return this.http.post<any>(this.stockBaseUrl, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            catchError((error) => {
                throw error;
            })
        );
    }


    setStockQuantity(stockData: {
        entrepot_id: number;
        product_id: number;
        quantity: number;
    }): Observable<any>
    {

        const payload: Record<string, unknown> = {
            entrepot_id: stockData.entrepot_id,
            product_id: stockData.product_id,
            quantity: stockData.quantity.toString()
        };
        this.applySessionToPayloadBody(payload);

        const url = `${this.stockBaseUrl}/set-quantity`;
        return this.http.post<any>(url, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            map(response => {
                return response;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    getStockByProductAndEntrepot(productId: number, entrepotId: number): Observable<any> {
        const params = this.appendStationUserQueryParams(
            new HttpParams()
                .set('product_id', productId.toString())
                .set('entrepot_id', entrepotId.toString())
        );

        const url = `${this.stockBaseUrl}?product_id=${productId}&entrepot_id=${entrepotId}`;
        return this.http.get<any>(this.stockBaseUrl, { params }).pipe(
            map(response => {

                if (response?.success === true && response?.data?.stocks && Array.isArray(response.data.stocks)) {
                    const stock = response.data.stocks.find((s: any) => 
                        s.product_id === productId && s.entrepot_id === entrepotId
                    ) || response.data.stocks[0] || null;

                    return stock || { quantity: 0 };
                }
                
                if (response?.success === true && response?.data && (!response.data.stocks || (Array.isArray(response.data.stocks) && response.data.stocks.length === 0))) {
                    return { quantity: 0 };
                }
                
                if (Array.isArray(response?.data)) {
                    const stock = response.data.find((s: any) => 
                        s.product_id === productId && s.entrepot_id === entrepotId
                    ) || response.data[0] || null;
                    return stock || { quantity: 0 };
                }
                
                const stock = response?.data || response || null;
                return stock && typeof stock === 'object' ? stock : { quantity: 0 };
            }),
            catchError((error) => {
                if (error?.status === 404) {
                }
                return of({ quantity: 0 });
            })
        );
    }

    getStockByEntrepot(entrepotId: number, page: number = 1, limit: number = 1000): Observable<{ success: boolean; data: { stocks: any[]; pagination?: any } }> {
        const url = `${this.stockBaseUrl}/entrepot/${entrepotId}`;
        const params = this.appendStationUserQueryParams(
            new HttpParams().set('page', String(page)).set('limit', String(limit))
        );
        return this.http.get<any>(url, { params }).pipe(
            map((res) => {
                let stocks: any[] = [];
                let pagination: any = null;
                if (Array.isArray(res?.data?.stocks)) {
                    stocks = res.data.stocks;
                    pagination = res.data.pagination ?? res.data.meta ?? null;
                } else if (Array.isArray(res?.data)) {
                    stocks = res.data;
                } else if (Array.isArray(res?.stocks)) {
                    stocks = res.stocks;
                }
                const ok = res?.success !== false;
                return { success: ok, data: { stocks, pagination } };
            }),
            catchError(() => of({ success: false, data: { stocks: [], pagination: null } }))
        );
    }

    getStockDetailsByProduct(productId: number): Observable<any> {
        const url = `${this.stockBaseUrl}/product/${productId}`;
        return this.http.get<any>(url, { params: this.appendStationUserQueryParams() }).pipe(
            map(response => {
                if (response?.success === true && response?.data) {
                    return response.data;
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    getTotalStockByProduct(
        productPathId: number,
        debugLog?: { erpProductId?: number; resolvedStockId?: number | null }
    ): Observable<number> {
        const url = `${this.stockBaseUrl}/product/${productPathId}`;
        return this.http.get<any>(url, { params: this.appendStationUserQueryParams() }).pipe(
            map(response => {
                if (response?.success === false) {
                    return 0;
                }

                if (response?.success === true && response?.data) {
                    if (response.data.total_quantity !== undefined && response.data.total_quantity !== null) {
                        const totalQuantity = Number(response.data.total_quantity);
                        return Number.isFinite(totalQuantity) ? totalQuantity : 0;
                    }
                }

                let stocks: any[] = [];
                if (response?.data?.stocks_by_entrepot && Array.isArray(response.data.stocks_by_entrepot)) {
                    stocks = response.data.stocks_by_entrepot;
                } else if (Array.isArray(response)) {
                    stocks = response;
                } else if (Array.isArray(response?.data)) {
                    stocks = response.data;
                }

                if (stocks.length > 0) {
                    const totalQuantity = stocks.reduce(
                        (sum: number, stock: any) => sum + Number(stock?.quantity ?? stock?.qty ?? 0),
                        0
                    );
                    return totalQuantity;
                }

                return 0;
            }),
            catchError(() => of(0))
        );
    }


    getStockTotalQuantityByProductId(productId: number): Observable<number> {
        const url = `${this.stockBaseUrl}/product/${productId}`;
        return this.http.get<any>(url, { params: this.appendStationUserQueryParams() }).pipe(
            map(response => {
                if (response?.success === true && response?.data) {
                    const totalQuantity = Number(response.data.total_quantity || 0);
                    return totalQuantity;
                }
                if (response?.data?.stocks_by_entrepot && Array.isArray(response.data.stocks_by_entrepot)) {
                    const totalQuantity = response.data.stocks_by_entrepot.reduce((sum: number, stock: any) => {
                        return sum + Number(stock?.quantity || 0);
                    }, 0);
                    return totalQuantity;
                }
                return 0;
            }),
            catchError((error) => {
                return of(0);
            })
        );
    }

    createMouvementStock(mouvementData: {
        product_id: number;
        entrepot_id: number;
        entrepot_dest_id?: number | null;
        type: string;
        quantite: number;
        date: string;
        reference_document?: string | null;
        document_id?: number | null;

        stock_avant?: number | null;
        stock_apres?: number | null;
        station_id?: number;
        user_id?: number;
    }): Observable<any>
    {
        let formattedDate = mouvementData.date;
        if (!formattedDate.includes('T')) {
            formattedDate = `${formattedDate}T12:00:00Z`;
        } else if (!formattedDate.endsWith('Z') && !formattedDate.includes('+')) {
            formattedDate = formattedDate.endsWith('Z') ? formattedDate : `${formattedDate}Z`;
        }

        const payload: any = {
            product_id: mouvementData.product_id,
            entrepot_id: mouvementData.entrepot_id,
            type: mouvementData.type,
            quantite: mouvementData.quantite.toString(),
            date: formattedDate,
            reference_document: (mouvementData.reference_document && mouvementData.reference_document.trim() !== '') 
                ? mouvementData.reference_document 
                : null,
            document_id: (mouvementData.document_id !== undefined && mouvementData.document_id !== null) 
                ? mouvementData.document_id 
                : null
        };
        if (mouvementData.stock_avant != null && mouvementData.stock_apres != null) {
            payload.stock_avant = String(mouvementData.stock_avant);
            payload.stock_apres = String(mouvementData.stock_apres);
        }
        if (mouvementData.entrepot_dest_id !== undefined && mouvementData.entrepot_dest_id !== null) {
            payload.entrepot_dest_id = mouvementData.entrepot_dest_id;
        }

        const ctx = this.getStationUserIdsFromStorage();
        const stationId =
            mouvementData.station_id != null && mouvementData.station_id > 0
                ? mouvementData.station_id
                : ctx.stationId;
        const userId =
            mouvementData.user_id != null && mouvementData.user_id > 0
                ? mouvementData.user_id
                : ctx.userId;
        if (stationId > 0) {
            payload.station_id = stationId;
        }
        if (userId > 0) {
            payload.user_id = userId;
        }

        return this.http.post<any>(this.mouvementBaseUrl, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            catchError((error) => {
                throw error;
            })
        );
    }


    createProduct(productData: {
        id: number;
        ref_interne?: string;
        nom: string;
        prix_achat: number;
        prix_vente: number;
    }): Observable<any>
    {
        const payload: Record<string, unknown> = {
            product_id: productData.id,
            ref_interne: productData.ref_interne || '',
            nom: productData.nom,
            prix_achat: productData.prix_achat.toString(),
            prix_vente: productData.prix_vente.toString()
        };
        this.applySessionToPayloadBody(payload);
        return this.http.post<any>(this.productBaseUrl, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            map(response => {
                const stockProductId = response?.data?.id || response?.id || response?.product?.id;
                
                if (!stockProductId) {
                    throw new Error('ID du produit module stock non trouvé dans la réponse de création');
                }

                return {
                    ...response,
                    stockProductId: stockProductId
                };
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    updateProductPrices(productId: number, prixAchat: number, prixVente: number, mainProductId?: number): Observable<any> {
        const payload: any = {
            prix_achat: prixAchat.toString(),
            prix_vente: prixVente.toString()
        };
        
        if (mainProductId !== undefined && mainProductId !== null) {
            payload.product_id = mainProductId;
        }
        
        const url = `${this.productBaseUrl}/${productId}`;
        return this.http.put<any>(url, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            map(response => {

                let stockProductId = response?.data?.id || response?.id || response?.product?.id;

                if (!stockProductId) {
                    stockProductId = productId;
                }
                
                return {
                    ...response,
                    stockProductId: stockProductId
                };
            }),
            catchError((error) => {
                throw error;
            })
        );
    }


    getProductByProductId(productId: number): Observable<any> {
        const params = this.appendStationUserQueryParams(
            new HttpParams().set('product_id', productId.toString())
        );
        const url = `${this.productBaseUrl}?product_id=${productId}`;

        return this.http.get<any>(this.productBaseUrl, { params }).pipe(
            map(response => {
                let product = null;
                
                if (Array.isArray(response)) {
                    product = response.find((p: any) => p.product_id === productId) || null;
                } else if (Array.isArray(response.data)) {
                    product = response.data.find((p: any) => p.product_id === productId) || null;
                } else if (response.data && !Array.isArray(response.data)) {
                    if (response.data.product_id === productId) {
                        product = response.data;
                    }
                } else if (response && response.product_id === productId) {
                    product = response;
                }
                

                
                return product;
            }),
            this.catchHttpNotFoundAsNull<any>()
        );
    }


    getStockProductIdByProductId(productId: number): Observable<number | null> {
        const url = `${this.productBaseUrl}/by-product-id/${productId}`;

        return this.http.get<any>(url, { params: this.appendStationUserQueryParams() }).pipe(
            map(response => {

                if (response?.success === false) {
                    return null;
                }

                const payload = response?.data ?? response;
                const okEnvelope =
                    response?.success === true ||
                    response?.success === undefined ||
                    response?.success === 1;
                if (payload && typeof payload === 'object' && okEnvelope) {
                    const rawId = payload.stock_product_id ?? payload.id;
                    const numId = typeof rawId === 'number' ? rawId : (typeof rawId === 'string' ? parseInt(rawId, 10) : NaN);
                    if (!isNaN(numId)) {
                        return numId;
                    }
                }

                return null;
            }),
            this.catchHttpNotFoundAsNull<number>()
        );
    }

    getStockProductIdByProductIdWithFallback(productId: number): Observable<number | null> {
        return this.getStockProductIdByProductId(productId).pipe(
            switchMap((id) => {
                if (id !== null && id !== undefined) return of(id);
                return this.getProductByProductId(productId).pipe(
                    map((product) => (product?.id != null ? product.id : null))
                );
            })
        );
    }

    getStockProductByProductIdPath(erpProductId: number): Observable<any | null> {
        const url = `${this.productBaseUrl}/product-id/${erpProductId}`;
        return this.http.get<any>(url, { params: this.appendStationUserQueryParams() }).pipe(
            map((response) => {
                if (response?.success === false) {
                    const msg = response?.message != null ? String(response.message) : '';

                    return null;
                }
                const raw = response?.data !== undefined ? response.data : response;
                if (raw == null || typeof raw === 'boolean') return null;
                return raw;
            }),
            catchError((error) => {
                if (error?.status === 404) {
                    return of(null);
                }
                return of(null);
            })
        );
    }

    private bootstrapStockProductForErpId(erpProductId: number): Observable<number | null> {
        return this.http.get<any>(`${this.erpCatalogProductsUrl}${erpProductId}`).pipe(
            catchError(() => of(null)),
            switchMap((erpRes) => {
                const p = erpRes?.data ?? erpRes ?? {};
                const refInterne =
                    String(p?.ref_interne ?? p?.reference ?? '').trim() || `ERP-${erpProductId}`;
                const nom = String(p?.nom ?? p?.designation ?? '').trim() || `Produit #${erpProductId}`;
                const prixAchat =
                    Number(p?.achat_ht ?? p?.prix_unitaires ?? p?.prix_achat ?? 0) || 0;
                const prixVente =
                    Number(p?.vente_ht ?? p?.prix_vente ?? p?.result_ht ?? prixAchat) ||
                    Math.max(prixAchat, 0.001);
                return this.createProduct({
                    id: erpProductId,
                    ref_interne: refInterne,
                    nom,
                    prix_achat: prixAchat,
                    prix_vente: prixVente,
                }).pipe(
                    catchError((err: any) => {
                        const exRaw = err?.error?.existing_product_id;
                        if (err?.status === 400 && exRaw != null) {
                            const ex = Number(exRaw);
                            if (!Number.isFinite(ex) || ex <= 0) {
                                return throwError(() => err);
                            }
                            // Même ref_interne sans lien product_id ERP → associer le catalogue (334)
                            return this.updateProductPrices(ex, prixAchat, prixVente, erpProductId).pipe(
                                map((ur) => ({
                                    stockProductId:
                                        ur?.stockProductId ?? ur?.data?.id ?? ur?.id ?? ur?.product?.id ?? ex,
                                })),
                                catchError(() => of({ stockProductId: ex }))
                            );
                        }
                        return throwError(() => err);
                    })
                );
            }),
            map((r: any) => {
                const sid = r?.stockProductId ?? r?.data?.id ?? r?.id ?? r?.product?.id;
                return sid != null && !isNaN(Number(sid)) ? Number(sid) : null;
            }),
            catchError(() => of(null))
        );
    }


    private resolveStockTableProductIdCacheKey(
        erpProductId: number,
        options?: { entrepotIdForZeroStock?: number | null } | null
    ): string {
        const eid = options?.entrepotIdForZeroStock;
        return `${erpProductId}:${eid != null ? eid : ''}`;
    }

    resolveStockTableProductIdFromErpId(
        erpProductId: number,
        options?: { entrepotIdForZeroStock?: number | null } | null
    ): Observable<number | null> {
        const cacheKey = this.resolveStockTableProductIdCacheKey(erpProductId, options);
        const cached = this.erpStockResolveCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const request$ = this.resolveStockTableProductIdFromErpIdInternal(erpProductId, options).pipe(
            shareReplay(1),
            finalize(() => this.erpStockResolveCache.delete(cacheKey))
        );
        this.erpStockResolveCache.set(cacheKey, request$);
        return request$;
    }

    private resolveStockTableProductIdFromErpIdInternal(
        erpProductId: number,
        options?: { entrepotIdForZeroStock?: number | null } | null
    ): Observable<number | null> {
        return this.getStockProductByProductIdPath(erpProductId).pipe(
            switchMap((meta) => {
                const id = this.parseStockTableProductId(meta);
                if (id != null) {
                    return of({ stockLocalId: id, bootstrapped: false });
                        }
                        return this.bootstrapStockProductForErpId(erpProductId).pipe(
                            map((createdId) => ({
                                stockLocalId: createdId,
                                bootstrapped: createdId != null
                            }))
                );
            }),
            switchMap(({ stockLocalId, bootstrapped }) => {
                if (stockLocalId == null) {
                    return of(null);
                }
                const eid = options?.entrepotIdForZeroStock;
                const entrepotNum = eid != null ? Number(eid) : NaN;
                if (!bootstrapped || !Number.isFinite(entrepotNum) || entrepotNum <= 0) {
                    return of(stockLocalId);
                }
                return this.createOrUpdateStock({
                    entrepot_id: entrepotNum,
                    product_id: stockLocalId,
                    quantity: 0
                }).pipe(
                    map(() => stockLocalId),
                    catchError(() => of(stockLocalId))
                );
            })
        );
    }

    /**
     * Détail stock pour un produit ERP (id = GET /api/products/station → data[].id).
     * Résout products_stock.id via product_id avant d'appeler GET /api/stock/product/:id
     */
    getStockDetailsByErpProductId(erpProductId: number): Observable<any> {
        const id = Number(erpProductId);
        if (!Number.isFinite(id) || id <= 0) {
            return of(null);
        }
        return this.getStockProductIdByProductIdWithFallback(id).pipe(
            switchMap((stockId) => {
                if (stockId != null && stockId > 0) {
                    return this.getStockDetailsByProduct(stockId);
                }
                return of(null);
            }),
            catchError(() => of(null))
        );
    }

    getStockAlertSnapshotByErpProductId(erpProductId: number): Observable<{
        totalQuantity: number;
        stocksByEntrepot: { entrepotId: number; quantity: number }[];
        stockProductId: number | null;
        stockTableProductId: number | null;
    } | null> {
        return this.getStockDetailsByErpProductId(erpProductId).pipe(
            map((data: any) => {
                if (!data || typeof data !== 'object') {
                    return null;
                }
                const totalQuantity = Number(data.total_quantity ?? 0);
                const raw = data.stocks_by_entrepot;
                const stocksByEntrepot: { entrepotId: number; quantity: number }[] = Array.isArray(raw)
                    ? raw.map((s: any) => ({
                          entrepotId: Number(s.entrepot_id ?? s.entrepotId ?? 0),
                          quantity: Number(s.quantity ?? s.qty ?? 0)
                      }))
                    : [];
                const sp = data.stock_product_id;
                const stockProductId =
                    sp !== undefined && sp !== null && !isNaN(Number(sp)) ? Number(sp) : null;
                const pid = data.product?.id ?? data.product_id;
                const stockTableProductId =
                    pid !== undefined && pid !== null && !isNaN(Number(pid)) ? Number(pid) : null;
                return { totalQuantity, stocksByEntrepot, stockProductId, stockTableProductId };
            })
        );
    }


    /**
     * total_quantity pour un produit actif ERP (biblio / document).
     * Le backend résout products.id → products_stock via product_id (pas besoin d'appel by-product-id).
     */
    getTotalStockByErpProductId(erpProductId: number): Observable<number> {
        const id = Number(erpProductId);
        if (!Number.isFinite(id) || id <= 0) {
            return of(0);
        }
        return this.getStockDetailsByErpProductId(id).pipe(
            map((data) => {
                if (!data || typeof data !== 'object') {
                    return 0;
                }
                const total = Number(data.total_quantity ?? 0);
                return Number.isFinite(total) ? total : 0;
            }),
            catchError(() => of(0))
        );
    }

    /**
     * Totaux stock pour une liste d'IDs catalogue ERP — 1 (ou peu) requête(s) batch.
     * POST /api/stock/totals-by-erp-products
     */
    getTotalStockByErpProductIds(erpProductIds: number[]): Observable<Record<number, number>> {
        const ids = [
            ...new Set(
                (erpProductIds || [])
                    .map((x) => Number(x))
                    .filter((n) => Number.isInteger(n) && n > 0)
            )
        ];
        if (ids.length === 0) {
            return of({});
        }

        const CHUNK = 500;
        const chunks: number[][] = [];
        for (let i = 0; i < ids.length; i += CHUNK) {
            chunks.push(ids.slice(i, i + CHUNK));
        }

        const { stationId, userId } = this.getStationUserIdsFromStorage();
        const url = `${this.stockBaseUrl}/totals-by-erp-products`;

        const requests = chunks.map((chunk) =>
            this.http
                .post<any>(url, {
                    product_ids: chunk,
                    station_id: stationId,
                    user_id: userId
                })
                .pipe(
                    map((response) => {
                        const out: Record<number, number> = {};
                        const raw = response?.data?.totals ?? {};
                        if (raw && typeof raw === 'object') {
                            for (const [k, v] of Object.entries(raw)) {
                                const pid = Number(k);
                                const qty = Number(v);
                                if (Number.isFinite(pid) && pid > 0) {
                                    out[pid] = Number.isFinite(qty) ? qty : 0;
                                }
                            }
                        }
                        for (const id of chunk) {
                            if (out[id] === undefined) {
                                out[id] = 0;
                            }
                        }
                        return out;
                    }),
                    catchError(() => {
                        const fallback: Record<number, number> = {};
                        for (const id of chunk) {
                            fallback[id] = 0;
                        }
                        return of(fallback);
                    })
                )
        );

        return forkJoin(requests).pipe(
            map((parts) => {
                const merged: Record<number, number> = {};
                for (const part of parts) {
                    Object.assign(merged, part);
                }
                return merged;
            })
        );
    }

    private parseStockTableProductId(row: any): number | null {
        if (!row || typeof row !== 'object') return null;
        const candidates = [
            row.stock_product_id,
            row.product_local_id,
            row.product?.id,
            row.stock_product?.id,
            row.id
        ];
        for (const raw of candidates) {
            if (raw === undefined || raw === null) continue;
            const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
            if (!isNaN(n)) return n;
        }
        return null;
    }

    private extractMouvementsArrayFromResponse(response: any): any[] {
        if (response == null) return [];
        if (Array.isArray(response)) return response;

        const tryPick = (node: any): any[] | null => {
            if (node == null) return null;
            if (Array.isArray(node)) return node;
            if (Array.isArray(node?.mouvements)) return node.mouvements;
            if (Array.isArray(node?.rows)) return node.rows;
            if (Array.isArray(node?.items)) return node.items;
            if (Array.isArray(node?.data)) return node.data;
            if (Array.isArray(node?.result)) return node.result;
            if (Array.isArray(node?.records)) return node.records;
            if (Array.isArray(node?.list)) return node.list;
            return null;
        };

        let picked =
            tryPick(response) ??
            tryPick(response?.data) ??
            (response?.data?.data && tryPick(response.data)) ??
            null;

        if (picked) return picked;

        // Dernier recours : premier tableau trouvé sous data (y compris tableaux vides)
        const data = response?.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            for (const k of Object.keys(data)) {
                const v = (data as any)[k];
                if (Array.isArray(v)) return v;
            }
        }
        return [];
    }


    getStockContextForMouvements(erpProductId: number): Observable<{ stockId: number | null; meta: any | null }> {
        return this.resolveStockTableProductIdFromErpId(erpProductId).pipe(
            switchMap((stockId) => {
                if (stockId == null) {
                    return of({ stockId: null, meta: null });
                }
                return this.getStockProductByProductIdPath(erpProductId).pipe(
                    map((meta) => ({
                        stockId,
                        meta: meta && typeof meta === 'object' ? meta : { id: stockId }
                    })),
                    catchError(() => of({ stockId, meta: { id: stockId } }))
                );
            })
        );
    }


    getMouvementsStockByStockProductId(
        stockProductId: number,
        opts?: { stationId?: number; userId?: number }
    ): Observable<any[]> {
        const params = this.buildStationUserQueryParams(opts);
        const url = `${this.mouvementBaseUrl}/product/${stockProductId}`;
        return this.http.get<any>(url, { params }).pipe(
            map((response) => this.extractMouvementsArrayFromResponse(response)),
            catchError((error) => {
                return of([]);
            })
        );
    }

    /**
     * Mouvements de stock filtrés par produit (rapport « Mouvement détaillé de stock »).
     *
     * Étapes :
     *  1. GET /api/stock/product/:erpProductId?station_id=&user_id=
     *     → on récupère le `id` du produit dans le champ `data.product` (ou `product`)
     *  2. GET /api/mouvements-stock/product/:stockProductId?station_id=&user_id=
     *
     * Retourne le tableau brut des mouvements (vide si l'étape 1 ne renvoie rien).
     */
    getMouvementsStockByErpProductId(
        erpProductId: number,
        opts?: { stationId?: number; userId?: number }
    ): Observable<any[]> {
        if (!Number.isFinite(erpProductId) || erpProductId <= 0) {
            return of([]);
        }
        const stockUrl = `${this.stockBaseUrl}/product/${erpProductId}`;
        const queryParams = this.buildStationUserQueryParams(opts);
        return this.http.get<any>(stockUrl, { params: queryParams }).pipe(
            switchMap((stockRes: any) => {
                const productNode =
                    stockRes?.data?.product ??
                    stockRes?.product ??
                    stockRes?.data ??
                    null;
                const candidates = [
                    productNode?.id,
                    stockRes?.data?.product_id,
                    stockRes?.product_id,
                    stockRes?.data?.id,
                    stockRes?.id,
                ];
                let stockProductId: number | null = null;
                for (const raw of candidates) {
                    if (raw == null) continue;
                    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
                    if (Number.isFinite(n) && n > 0) {
                        stockProductId = n;
                        break;
                    }
                }
                if (stockProductId == null) {
                    return of([]);
                }
                const movUrl = `${this.mouvementBaseUrl}/product/${stockProductId}`;
                return this.http.get<any>(movUrl, { params: queryParams }).pipe(
                    map((mouvementsRes) => this.extractMouvementsArrayFromResponse(mouvementsRes))
                );
            }),
            catchError(() => of([]))
        );
    }

    /**
     * Liste paginée des mouvements de stock (rapport « Mouvement détaillé de stock »).
     * GET /api/mouvements-stock?page=&limit=&product_id=&entrepot_id=&station_id=&user_id=
     */
    getMouvementsStock(opts?: {
        page?: number;
        limit?: number;
        product_id?: number;
        entrepot_id?: number;
        stationId?: number;
        userId?: number;
    }): Observable<any> {
        let params = this.buildStationUserQueryParams({
            stationId: opts?.stationId,
            userId: opts?.userId,
        });
        params = params.set('page', String(opts?.page ?? 1));
        params = params.set('limit', String(opts?.limit ?? 50));
        if (opts?.product_id != null && opts.product_id > 0) {
            params = params.set('product_id', String(opts.product_id));
        }
        if (opts?.entrepot_id != null && opts.entrepot_id > 0) {
            params = params.set('entrepot_id', String(opts.entrepot_id));
        }
        return this.http.get<any>(this.mouvementBaseUrl, { params }).pipe(
            catchError((error) => {
                throw error;
            })
        );
    }

    createStockTransfert(payload: {
        user_id: number;
        station_id: number;
        date_transfert: string;
        entrepot_id_depart: number;
        entrepot_id_reception: number;
        origine: string;
        note: string;
        lignes: Array<{
            libelle_article: string;
            reference: string;
            allocations: Array<{
                quantite: number;
                product_id: number;
                code_barre: string;
            }>;
        }>;
    }): Observable<any> {
        const url = this.stockTransfertsUrl;
        const requete = { method: 'POST' as const, url, body: payload };

        return this.http.post<any>(url, payload).pipe(

            catchError((error) => {
                throw error;
            })
        );
    }

    transferStock(transferData: {
        entrepot_initial_id: number;
        entrepot_dest_id: number;
        product_id: number;
        quantity: number;
        document_id?: number | null;
        reference_document?: string | null;
    }): Observable<any>
    {
        const payload: Record<string, unknown> = {
            entrepot_initial_id: transferData.entrepot_initial_id,
            entrepot_dest_id: transferData.entrepot_dest_id,
            product_id: transferData.product_id,
            quantity: transferData.quantity
        };
        if (transferData.document_id != null && transferData.document_id !== undefined) {
            payload.document_id = transferData.document_id;
        }
        const ref = transferData.reference_document != null ? String(transferData.reference_document).trim() : '';
        if (ref !== '') {
            payload.reference_document = ref;
        }
        this.applySessionToPayloadBody(payload);

        const url = `${this.stockBaseUrl}/transfer`;

        return this.http.post<any>(url, payload, {
            params: this.appendStationUserQueryParams()
        }).pipe(
            map(response => {
                return response;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }


    getProductByRefInterne(refInterne: string): Observable<any> {
        const params = this.appendStationUserQueryParams(new HttpParams().set('ref_interne', refInterne));
        const url = `${this.productBaseUrl}?ref_interne=${refInterne}`;
        return this.http.get<any>(this.productBaseUrl, { params }).pipe(
            map(response => {
                let product = null;
                
                if (Array.isArray(response)) {
                    product = response.find((p: any) => p.ref_interne === refInterne) || null;
                } else if (Array.isArray(response.data)) {
                    product = response.data.find((p: any) => p.ref_interne === refInterne) || null;
                } else if (response.data && !Array.isArray(response.data)) {
                    if (response.data.ref_interne === refInterne) {
                        product = response.data;
                    }
                } else if (response && response.ref_interne === refInterne) {
                    product = response;
                }

                
                return product;
            }),
        );
    }
}
